import { clearLoading } from "../walk/memory";
import { registerSW } from "virtual:pwa-register";
import {
  UpdateFlow,
  parseVersionFile,
  type AttemptRecord,
  type AttemptStore,
  type RegistrationLike,
  type UpdateStatus,
} from "./updateFlow";

/** How often an open app asks the server for a newer service worker. */
const CHECK_INTERVAL_MS = 30 * 60 * 1000;
const ATTEMPT_KEY = "walk-player:update-attempt";

export type OfflineState = "ready" | "unavailable";

export interface UpdateCallbacks {
  onStatus(status: UpdateStatus): void;
  onOfflineState(state: OfflineState): void;
  /** True when reloading now would interrupt nothing (no walk open/opening). */
  isIdle(): boolean;
}

/** sessionStorage, which survives the reload an update does; unavailable storage just disables the loop guard. */
const attemptStore: AttemptStore = {
  read() {
    try {
      const record = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY) ?? "null") as AttemptRecord | null;
      return record && typeof record.from === "string" ? record : null;
    } catch {
      return null;
    }
  },
  write(record) {
    try {
      sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(record));
    } catch {
      // Private mode / blocked storage: no loop guard, the update still works.
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(ATTEMPT_KEY);
    } catch {
      // see write()
    }
  },
};

/** The deployed version (version.json is emitted by the build and never precached), past every cache. */
async function fetchLatestVersion(): Promise<string | null> {
  const url = new URL("version.json", document.baseURI);
  url.searchParams.set("t", String(Date.now()));
  const response = await fetch(url, { cache: "no-store" });
  return response.ok ? parseVersionFile(await response.text()) : null;
}

/** Resolves true on the next controllerchange, false after `timeoutMs`. */
function nextControllerChange(timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (changed: boolean): void => {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      resolve(changed);
    };
    const onChange = (): void => done(true);
    const timer = setTimeout(() => done(false), timeoutMs);
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
  });
}

/**
 * Keeps the app on the latest release without ever risking offline use, and
 * backs the home screen's "Check for updates" / "Update now" button.
 *
 * A new service worker only reaches "waiting" after its whole precache has
 * downloaded (Workbox installs atomically). Until then - or if the download
 * fails, or the device is offline - the current worker and its complete
 * cache keep serving. Once one is waiting it is applied immediately if the
 * app is idle, otherwise as soon as it becomes idle (see applyIfIdle).
 * The logic lives in updateFlow.ts; this file only wires it to the browser.
 */
export class AppUpdater {
  private readonly flow: UpdateFlow;
  private registration: ServiceWorkerRegistration | null = null;

  constructor(callbacks: UpdateCallbacks) {
    this.flow = new UpdateFlow(
      {
        currentVersion: __APP_VERSION__,
        getRegistration: async () =>
          (this.registration ?? (await navigator.serviceWorker.getRegistration())) as RegistrationLike | null,
        fetchLatestVersion,
        cacheStorage: "caches" in window ? caches : undefined,
        controllerChange: nextControllerChange,
        reload: () => {
          clearLoading(localStorage); // an app-initiated reload is not a crash of a loading walk
          location.reload();
        },
        isIdle: () => callbacks.isIdle(),
        attempts: attemptStore,
        now: () => Date.now(),
      },
      (status) => callbacks.onStatus(status),
    );
    this.flow.settle();
    registerSW({
      immediate: true,
      onNeedRefresh: () => {
        this.flow.workerWaiting();
        this.applyIfIdle();
      },
      onOfflineReady: () => callbacks.onOfflineState("ready"),
      onRegisteredSW: (_url, registration) => {
        if (!registration) return;
        this.registration = registration;
        // Refills the offline copy if it lost files (a cache deleted under a running worker).
        registration.active?.postMessage({ type: "CHECK_PRECACHE" });
        // No waiting worker after registration: this version is the one installed.
        if (!registration.waiting && !registration.installing) callbacks.onOfflineState("ready");
        const check = (): void => {
          if (!navigator.onLine || registration.installing) return;
          // Offline / server errors just leave the current version in place.
          registration.update().catch(() => undefined);
        };
        setInterval(check, CHECK_INTERVAL_MS);
        addEventListener("online", check);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
      },
      onRegisterError: () => callbacks.onOfflineState("unavailable"),
    });
  }

  /** The button's "Check for updates". */
  check(): Promise<void> {
    return this.flow.check();
  }

  /** The button after a failure: repeats the check or the update that failed. */
  retry(): Promise<void> {
    return this.flow.retry();
  }

  /** The button's "Update now": switches to the new version and reloads. */
  apply(): Promise<void> {
    return this.flow.apply();
  }

  /** Switches to the waiting version (reloads the page) if one is ready and nothing is open. */
  applyIfIdle(): void {
    if (this.flow.status.phase === "ready") void this.flow.apply({ auto: true });
  }
}
